import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import LocationFeatures from '../../../components/LocationFeatures';
import { LocationWizardData } from '../types';

interface LocationFeaturesStepProps {
  data: LocationWizardData;
  updateData: (partial: Partial<LocationWizardData>) => void;
}

/** Features shown in step 2 and managed there — exclude from this grid. */
const EXCLUDED_FEATURES = new Set(['24/7 Access']);

const FEATURE_OPTIONS = LocationFeatures.OPTIONS.filter(
  (f) => !EXCLUDED_FEATURES.has(f)
);

/**
 * Explicit two-column distribution per design spec (7 left, 4 right).
 * Items not in either list fall back to the left column.
 */
const LEFT_COLUMN_FEATURES = [
  'Manned Security',
  'Fenced / Gated',
  'Yard Cameras',
  'Gate Cameras',
  'Lights',
  'M & R Offered',
  'Overnight Truck Parking',
];

const RIGHT_COLUMN_FEATURES = [
  'Paved',
  'Daily Inventory Report',
  'Side-pick / Top-Pick',
  'Restrooms',
];

/**
 * Split feature options into left/right columns using the explicit design order.
 *
 * Any option present in `LocationFeatures.OPTIONS` but missing from both column
 * arrays (e.g. a newly added feature, or one renamed so it no longer matches) is
 * appended to the left column instead of silently disappearing from the UI. A
 * dev-only warning flags the stale mapping so it can be corrected.
 */
const leftFeatures = LEFT_COLUMN_FEATURES.filter((f) => FEATURE_OPTIONS.includes(f));
const rightFeatures = RIGHT_COLUMN_FEATURES.filter((f) => FEATURE_OPTIONS.includes(f));

const mappedFeatures = new Set([...leftFeatures, ...rightFeatures]);
const unmappedFeatures = FEATURE_OPTIONS.filter((f) => !mappedFeatures.has(f));

if (unmappedFeatures.length > 0) {
  // Append rather than drop, so the supplier can always toggle every feature.
  leftFeatures.push(...unmappedFeatures);
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `LocationFeaturesStep: features not assigned to a column (rendered in the left column by default): ${unmappedFeatures.join(
        ', '
      )}. Update LEFT_COLUMN_FEATURES / RIGHT_COLUMN_FEATURES.`
    );
  }
}

/**
 * Step 4 (Features) of the Create/Edit Location wizard.
 *
 * Renders a two-column checkbox grid of location features (security, cameras,
 * paving, etc.). The "24/7 Access" feature is excluded because it is already
 * captured in Step 2 (Location Settings).
 */
const LocationFeaturesStep = (props: LocationFeaturesStepProps) => {
  const { data, updateData } = props;

  const selectedFeatures = new Set(
    (data.locationFeatures || []).map((f) => f.feature).filter(Boolean)
  );

  const toggleFeature = (feature: string) => {
    const existing = data.locationFeatures || [];
    const isSelected = existing.some((f) => f.feature === feature);

    const updated = isSelected
      ? existing.filter((f) => f.feature !== feature)
      : [...existing, { feature }];

    updateData({ locationFeatures: updated });
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Location Features
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select which features are available at this location.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: 3,
          rowGap: 0.5,
        }}
      >
        {/* Left column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {leftFeatures.map((feature) => (
            <FormControlLabel
              key={feature}
              control={
                <Checkbox
                  checked={selectedFeatures.has(feature)}
                  onChange={() => toggleFeature(feature)}
                />
              }
              label={feature}
            />
          ))}
        </Box>

        {/* Right column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {rightFeatures.map((feature) => (
            <FormControlLabel
              key={feature}
              control={
                <Checkbox
                  checked={selectedFeatures.has(feature)}
                  onChange={() => toggleFeature(feature)}
                />
              }
              label={feature}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default LocationFeaturesStep;
